import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Pencil, Plus, Search as SearchIcon, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminApi, type AdminProduct } from "@/features/admin/api";
import { formatApiError } from "@/features/auth/formatError";
import { Input } from "@/components/ui/input";
import { ROUTES } from "@/constants/routes";

const rupee = (n: number) => `₹${n.toLocaleString("en-IN")}`;

export default function AdminProducts() {
    const qc = useQueryClient();
    const [search, setSearch] = useState("");
    const [stock, setStock] = useState<string>("");
    const [page, setPage] = useState(1);
    const pageSize = 20;

    const { data, isLoading } = useQuery({
        queryKey: ["admin", "products", search, stock, page],
        queryFn: () =>
            adminApi.listProducts({
                search: search || undefined,
                stock: stock || undefined,
                page,
                page_size: pageSize,
            }),
        placeholderData: (prev) => prev,
    });

    const deleteMut = useMutation({
        mutationFn: (id: string) => adminApi.deleteProduct(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "products"] });
            toast.success("Product deleted");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't delete")),
    });

    const dupMut = useMutation({
        mutationFn: (id: string) => adminApi.duplicateProduct(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "products"] });
            toast.success("Duplicated — new draft added (inactive)");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't duplicate")),
    });

    const bulkMut = useMutation({
        mutationFn: (rows: Array<Record<string, unknown>>) => adminApi.bulkProducts(rows),
        onSuccess: (r) => {
            qc.invalidateQueries({ queryKey: ["admin", "products"] });
            const errCount = (r.errors || []).length;
            toast.success(
                `${r.created} products imported${errCount ? ` · ${errCount} skipped` : ""}`,
            );
        },
        onError: (err) => toast.error(formatApiError(err, "Bulk upload failed")),
    });

    const onBulkFile = async (file: File) => {
        const text = await file.text();
        const lines = text.split(/\r?\n/).filter(Boolean);
        if (lines.length < 2) {
            toast.error("CSV needs a header row + at least one product");
            return;
        }
        const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
        const rows = lines.slice(1).map((l) => {
            const cells = l.split(",");
            const row: Record<string, unknown> = {};
            header.forEach((h, i) => {
                row[h] = cells[i]?.trim() ?? "";
            });
            row.price = Number(row.price || 0);
            row.mrp = Number(row.mrp || row.price || 0);
            row.stock_count = Number(row.stock_count || 0);
            return row;
        });
        bulkMut.mutate(rows);
    };

    const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

    return (
        <div data-testid="admin-products-page" className="space-y-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
                        Products
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {data ? `${data.total} products in catalog` : "Loading…"}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-3 py-2 text-sm font-semibold hover:bg-muted">
                        <Upload className="h-4 w-4" />
                        {bulkMut.isPending ? "Uploading…" : "Bulk CSV"}
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            data-testid="admin-products-bulk-input"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) onBulkFile(f);
                                e.target.value = "";
                            }}
                        />
                    </label>
                    <Link
                        to={ROUTES.adminProductNew}
                        data-testid="admin-products-new"
                        className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white shadow-brand hover:bg-brand-600"
                    >
                        <Plus className="h-4 w-4" /> New product
                    </Link>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        className="pl-9"
                        placeholder="Search by name, brand or slug…"
                        data-testid="admin-products-search"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                </div>
                <select
                    value={stock}
                    onChange={(e) => {
                        setStock(e.target.value);
                        setPage(1);
                    }}
                    data-testid="admin-products-stock-filter"
                    className="h-10 rounded-full border border-border bg-background px-3 text-sm"
                >
                    <option value="">All stock</option>
                    <option value="in">In stock</option>
                    <option value="low">Low stock</option>
                    <option value="out">Out of stock</option>
                </select>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
                <table className="min-w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Product</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Price</th>
                            <th className="px-4 py-3">Stock</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                        {isLoading ? (
                            <tr>
                                <td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">
                                    Loading products…
                                </td>
                            </tr>
                        ) : data && data.items.length > 0 ? (
                            data.items.map((p) => <ProductRow key={p.id} product={p} onDelete={(id) => deleteMut.mutate(id)} onDuplicate={(id) => dupMut.mutate(id)} />)
                        ) : (
                            <tr>
                                <td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">
                                    No products found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span data-testid="admin-products-pagination-info">
                    Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className="rounded-full border border-border px-3 py-1 disabled:opacity-40"
                    >
                        Prev
                    </button>
                    <button
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                        className="rounded-full border border-border px-3 py-1 disabled:opacity-40"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}

function ProductRow({
    product,
    onDelete,
    onDuplicate,
}: {
    product: AdminProduct;
    onDelete: (id: string) => void;
    onDuplicate: (id: string) => void;
}) {
    const stockLabel =
        product.stock_count <= 0
            ? { text: "Out", tone: "text-error" }
            : product.stock_count <= product.low_stock_threshold
              ? { text: `Low · ${product.stock_count}`, tone: "text-amber-600" }
              : { text: String(product.stock_count), tone: "text-emerald-600" };

    return (
        <tr data-testid={`admin-product-row-${product.id}`}>
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    {product.images[0] ? (
                        <img
                            src={product.images[0]}
                            alt=""
                            className="h-9 w-9 rounded-lg object-cover"
                        />
                    ) : (
                        <span
                            className="grid h-9 w-9 place-items-center rounded-lg text-xs font-extrabold text-white"
                            style={{ background: `linear-gradient(135deg, ${product.tone[0]}, ${product.tone[1] || product.tone[0]})` }}
                        >
                            {product.glyph}
                        </span>
                    )}
                    <div className="min-w-0">
                        <p className="truncate font-semibold">{product.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                            {product.brand} · {product.weight}
                        </p>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3 text-xs text-muted-foreground">{product.category_name}</td>
            <td className="px-4 py-3 font-semibold">
                {rupee(product.price)}
                {product.mrp > product.price ? (
                    <span className="ml-2 text-xs text-muted-foreground line-through">
                        {rupee(product.mrp)}
                    </span>
                ) : null}
            </td>
            <td className={`px-4 py-3 text-sm font-semibold ${stockLabel.tone}`}>
                {stockLabel.text}
            </td>
            <td className="px-4 py-3">
                <span
                    className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${
                        product.is_active
                            ? "bg-emerald-500/10 text-emerald-700"
                            : "bg-slate-500/10 text-slate-500"
                    }`}
                >
                    {product.is_active ? "Live" : "Draft"}
                </span>
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                    <Link
                        to={ROUTES.adminProductEditBuild(product.id)}
                        data-testid={`admin-product-edit-${product.id}`}
                        className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted"
                        title="Edit"
                    >
                        <Pencil className="h-4 w-4" />
                    </Link>
                    <button
                        type="button"
                        onClick={() => onDuplicate(product.id)}
                        data-testid={`admin-product-duplicate-${product.id}`}
                        className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted"
                        title="Duplicate"
                    >
                        <Copy className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (window.confirm(`Delete "${product.name}"? This cannot be undone.`)) {
                                onDelete(product.id);
                            }
                        }}
                        data-testid={`admin-product-delete-${product.id}`}
                        className="grid h-8 w-8 place-items-center rounded-lg text-error hover:bg-error/10"
                        title="Delete"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </td>
        </tr>
    );
}
